import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { AuthService } from '@auth';
import { Organization } from '@shared/models/user-profile';
import { UnsyncedSummary } from '@shared/models/preferred-organization';
import { OnlineOfflineService } from '@core/services/online-offine.service';
import { PreferredOrganizationService } from '@core/services/preferred-organization.service';
import { UnsyncedRecordsService } from '@core/services/unsynced-records.service';
import {
  PreferredOrgChangeProgress,
  PreferredOrganizationChangeService
} from '@core/services/preferred-organization-change.service';

/**
 * Full-screen "Choose your preferred organization" page (US 27702), shown on first
 * login for multi-tenant users — and again when the saved preferred organization is
 * not permitted for the signed-in user and has nothing left to sync. The guard makes
 * it mandatory: no main route activates before a preferred organization exists.
 *
 * When the saved organization still has another user's pending records, the page
 * shows the counts-only pending notice instead of the picker, with a "Continue
 * online" way into the app (server-mode).
 *
 * A user with exactly one organization never sees the picker (Bug 28419): the page
 * starts the change flow for it right away and shows only the progress. Offline,
 * where the flow cannot start, the picker is shown with its offline hint instead.
 */
@Component({
  selector: 'app-select-organization',
  templateUrl: './select-organization.component.html',
  styleUrls: ['./select-organization.component.scss']
})
export class SelectOrganizationComponent implements OnInit, OnDestroy {

  view: 'loading' | 'pick' | 'progress' | 'pending' = 'loading';

  organizations: Organization[] = [];
  selectedKeycloakOrgId = '';
  isOnline = true;
  userName = '';

  progress?: PreferredOrgChangeProgress;

  /** Pending-notice state (saved preferred organization owned by another user). */
  blockedOrgName = '';
  pendingSummary?: UnsyncedSummary;

  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private authService: AuthService,
    private onlineOfflineService: OnlineOfflineService,
    private preferredOrganizationService: PreferredOrganizationService,
    private unsyncedRecordsService: UnsyncedRecordsService,
    private changeService: PreferredOrganizationChangeService
  ) { }

  async ngOnInit(): Promise<void> {
    this.isOnline = this.onlineOfflineService.isOnline;
    this.userName = (this.authService.userProfile?.name ?? '').trim();

    this.subscriptions.add(this.onlineOfflineService.connectionChanged.subscribe({
      next: () => this.isOnline = this.onlineOfflineService.isOnline
    }));

    this.subscriptions.add(this.changeService.progress$.subscribe({
      next: progress => {
        this.progress = progress;
        if (progress && progress.steps.length > 0) {
          this.view = 'progress';
        }
      }
    }));

    this.organizations = await this.authService.ensureOrganizationsLoaded();

    const preferred = this.preferredOrganizationService.preferred;
    if (preferred && !this.preferredOrganizationService.isPreferredPermittedFor(this.organizations)) {
      const state = this.unsyncedRecordsService.readPersistedState();
      const summary = this.unsyncedRecordsService.summarizePersistedState(state);
      if (summary.total > 0) {
        this.view = 'pending';
        this.blockedOrgName = preferred.name;
        this.pendingSummary = summary;
        return;
      }
    }

    this.selectedKeycloakOrgId = this.authService.currentOrganization?.keycloakOrgId
      || this.organizations[0]?.keycloakOrgId
      || '';

    if (this.organizations.length === 1 && this.isOnline && !this.changeService.inProgress) {
      // Bug 28419: nothing to pick. The change flow clears the previous user's data
      // first; it resolves only when it fails, so the picker then offers a retry.
      await this.changeService.changePreferredAsync(this.organizations[0], 'auto-single');
      if (this.view === 'loading') this.view = 'pick';
      return;
    }

    this.view = 'pick';
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  get selectedOrganization(): Organization | undefined {
    return this.organizations.find(org => org.keycloakOrgId === this.selectedKeycloakOrgId);
  }

  get hasFailed(): boolean {
    return !!this.progress?.errorKey;
  }

  select(organization: Organization): void {
    this.selectedKeycloakOrgId = organization.keycloakOrgId;
  }

  initialOf(organization: Organization): string {
    return (organization?.name || '?').charAt(0).toUpperCase();
  }

  async continueWithSelected(): Promise<void> {
    const target = this.selectedOrganization;
    if (!target || !this.isOnline || this.changeService.inProgress) return;
    await this.changeService.changePreferredAsync(target, 'picker');
  }

  backToPicker(): void {
    this.changeService.resetProgress();
    this.view = 'pick';
  }

  continueOnline(): void {
    this.router.navigate(['/']);
  }
}
